import { BdSectionTemplate } from "../../types/bd/Bd";
import { ResearchOrgNode } from "../../types/bd/Research";

/** Section HTML as plain text, one line per block (checks, word counts); the same reading as crx-be BdHtml.htmlText. */
export const plainText = (html: string): string =>
    String(html ?? "").replace(/<br\s*\/?>/gi, "\n").replace(/<li\b[^>]*>/gi, "\n- ")
        .replace(/<\/(p|li|h[1-6]|blockquote|tr|div|table|ul|ol)>/gi, "\n").replace(/<\/t[dh]>/gi, " ").replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#39;/g, "'").replace(/&amp;/g, "&")
        .replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();

/** What a section must cover, and whether its text covers it yet (any keyword). */
export function guideStatus(template: BdSectionTemplate, html: string): Array<{ t: string; ok: boolean }> {
    const text = plainText(html).toLowerCase();
    return template.guide.map((g) => ({ t: g.t, ok: !!text && g.k.some((k) => text.includes(k.toLowerCase())) }));
}

/** #rgb, #rrggbb or rgb(r, g, b) as #RRGGBB; transparent and anything else as "" (same rule as crx-be BdHtml.hexColor). */
export const hexColor = (value: string | null | undefined): string => {
    const v = String(value ?? "").trim().toLowerCase();
    const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(v);
    if (rgb) return rgb[4] !== undefined && Number(rgb[4]) === 0 ? "" : `#${[rgb[1], rgb[2], rgb[3]].map((n) => Math.min(255, Number(n)).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
    const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(v);
    if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toUpperCase();
    return /^#[0-9a-f]{6}$/.test(v) ? v.toUpperCase() : "";
};

/** The last value of a CSS property in an element's style attribute (lower case). */
export const styleValue = (el: Element, prop: string): string => {
    let out = "";
    (el.getAttribute("style") ?? "").split(";").forEach((d) => {
        const i = d.indexOf(":");
        if (i > 0 && d.slice(0, i).trim().toLowerCase() === prop) out = d.slice(i + 1).trim().toLowerCase();
    });
    return out;
};

/** Links a proposal may hold: web pages and email addresses. */
export const SAFE_LINK = /^(https?:\/\/|mailto:)[^\s"<>]+$/i;

const escapeHtml = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Replace the first occurrence of `find` in the text of `root`, even when it
 * runs across bold or coloured pieces: the new words take the formatting of
 * where the old ones start. Tags are never touched.
 */
export function replaceInText(root: Node, find: string, replacement: string): boolean {
    const doc = root.ownerDocument ?? document;
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n as Text);
    const starts: number[] = [];
    let all = "";
    nodes.forEach((n) => { starts.push(all.length); all += n.data; });
    const at = all.replace(/ /g, " ").indexOf(find.replace(/ /g, " "));
    if (!find || at < 0) return false;
    const end = at + find.length;
    let placed = false;
    nodes.forEach((n, i) => {
        const s = starts[i];
        const e = s + n.data.length;
        if (e <= at || s >= end) return;
        n.data = n.data.slice(0, Math.max(at, s) - s) + (placed ? "" : replacement) + n.data.slice(Math.min(end, e) - s);
        placed = true;
    });
    return true;
}

/** HTML parsed inertly (no images load, nothing runs): DOMParser, not innerHTML on a live element. */
const parseBody = (html: string): HTMLElement => new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, "text/html").body;

/**
 * Put a suggestion into a section: replace the words when found (first time,
 * inside the text so headings, lists, tables and colours stay), else add it
 * as a new paragraph at the end.
 * @returns The new HTML and whether the words were found.
 */
export function applySuggestion(html: string, find: string, suggestion: string): { html: string; found: boolean } {
    const sug = suggestion.trim();
    if (find) {
        const body = parseBody(html);
        if (replaceInText(body, find, sug)) return { html: body.innerHTML, found: true };
        if (!sug) return { html, found: false };
    }
    if (!sug) return { html, found: true };
    return { html: `${html}<p>${escapeHtml(sug)}</p>`, found: !find };
}

/** The org tree with the person at `path` (child indexes from the top) changed. */
export function setOrgNode(node: ResearchOrgNode, path: number[], patch: Partial<ResearchOrgNode>): ResearchOrgNode {
    if (!path.length) return { ...node, ...patch };
    const [at, ...rest] = path;
    return { ...node, children: node.children.map((c, i) => (i === at ? setOrgNode(c, rest, patch) : c)) };
}

/** Words in a section. */
export const wordCount = (html: string): number => (plainText(html).match(/\S+/g) ?? []).length;

/** [CONFIRM: …] placeholders still in the text. */
export const confirmsIn = (html: string): string[] => (plainText(html).match(/\[CONFIRM:[^\]]*\]/gi) ?? []);
